import React, { useCallback, useEffect, useRef, useState } from "react";
import LoadingIcon from "../LoadingIcon";
import {
  RootSVG,
  SVGApp,
  SVGAppContainer,
  SVGContainer,
  SVGViewerPill,
  Tools,
  Title,
} from "./SVGViewerStyles";
import ErrorComponent from "../../ErrorComponent";
import { ElementPlacement, MountType } from "../../../reducers/uploadedBoard";
import CursorLocation from "./CursorLocation";
import useSWR from "swr";
import useSWRFetcher from "../../../utils/hooks/commonly-stored/fetcher";
import { Response } from "next/dist/compiled/@edge-runtime/primitives";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/store";
import { setFocus } from "../../../reducers/svgViewer";

export const DEFAULT_FOOTPRINT_CURSOR = "/footprint-cursor.svg";

interface Overlay {
  input: SVGInput;
  placement: ElementPlacement;
  setPlacement?: (placement: ElementPlacement) => void;
}

export interface Position {
  x: number;
  y: number;
  zoom: number;
}

export interface ViewerLocation {
  x: number;
  y: number;
}

export interface Dimensions {
  width: number;
  height: number;
  minX: number;
  minY: number;
}

const DEFAULT_SCALE_AMOUNT = 0.1;

const determineRelativeScale = (
  svgDimensions: Dimensions,
  parentDimensions: Dimensions,
) => {
  const svgAspect = svgDimensions.height / svgDimensions.width;
  const parentAspect = parentDimensions.height / parentDimensions.width;
  if (svgAspect < parentAspect) {
    return parentDimensions.width / svgDimensions.width;
  } else {
    return parentDimensions.height / svgDimensions.height;
  }
};

const getDefaultPosition = (
  svgDimensions: Dimensions,
  parentDimensions: Dimensions,
  minScale?: number,
) => {
  const defaultRelativeScale = determineRelativeScale(
    svgDimensions,
    parentDimensions,
  );
  const relativeScale = minScale
    ? Math.min(minScale, defaultRelativeScale)
    : defaultRelativeScale;

  return {
    x: parentDimensions.width / 2 - (svgDimensions.width * relativeScale) / 2,
    y: parentDimensions.height / 2 - (svgDimensions.height * relativeScale) / 2,
    zoom: relativeScale,
  };
};

interface SVGInput {
  svg?: string;
  svgUrl?: string;
}

function useGetSVGElement(svgInput?: SVGInput) {
  const svg = svgInput?.svg;
  const svgUrl = svgInput?.svgUrl;
  const [svgElement, setSVGElement] = useState<HTMLElement | null>(null);
  const [svgDimensions, setSVGDimensions] = useState<Dimensions | null>(null);

  const parseSVG = useCallback((svgString: string) => {
    const parsedSVG = new DOMParser().parseFromString(
      svgString,
      "image/svg+xml",
    ).documentElement;
    const viewBox = parsedSVG.getAttribute("viewBox") as string;
    const [minX, minY, width, height] = viewBox
      .split(" ")
      .map((numberString) => parseFloat(numberString)) as [
      number,
      number,
      number,
      number,
    ];
    setSVGDimensions({
      width,
      height,
      minX,
      minY,
    });
    setSVGElement(parsedSVG);
  }, []);

  const fetcher = useSWRFetcher<string>(
    async (response: Response) => {
      return await response.text();
    },
    { noAuth: true },
  );

  const presignedFetcher = () => {
    return fetcher(svgUrl as string);
  };

  const isRemoteSVG = svgUrl !== undefined;

  const { data: fetchedSVG, error: fetchError } = useSWR<string, Error>(
    isRemoteSVG ? svgUrl.split("?")[0] : null,
    presignedFetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  useEffect(() => {
    if (isRemoteSVG) {
      if (fetchedSVG === undefined) {
        setSVGDimensions(null);
        setSVGElement(null);
      } else {
        parseSVG(fetchedSVG);
      }
    }
  }, [fetchedSVG, isRemoteSVG, parseSVG]);

  useEffect(() => {
    if (svg !== undefined) {
      parseSVG(svg);
    }
  }, [parseSVG, svg]);

  return {
    svgElement,
    svgDimensions,
    errored: fetchError !== undefined,
  };
}

enum MouseAction {
  Off,
  MoveBase,
  MoveOverlay,
}

export default function SVGViewer({
  svgInput,
  whiteBackground,
  mountType,
  overlay,
  frameless,
  inert,
  minScale,
  title,
}: {
  svgInput: SVGInput;
  whiteBackground?: boolean;
  mountType?: MountType;
  overlay?: Overlay;
  frameless?: boolean;
  inert?: boolean;
  minScale?: number;
  title?: string;
}) {
  const dispatch = useAppDispatch();
  const focus = useAppSelector((state) => state.svgViewer.focus);
  const [cursorLocation, setCursorLocation] = useState<ViewerLocation>({
    x: 0,
    y: 0,
  });
  const {
    svgElement: baseElement,
    svgDimensions: baseDimensions,
    errored: baseErrored,
  } = useGetSVGElement(svgInput);
  const {
    svgElement: overlayElement,
    svgDimensions: overlayDimensions,
    errored: overlayErrored,
  } = useGetSVGElement(overlay?.input);
  const borderWidth = frameless ? 0 : 2;

  const [mouseAction, setMouseAction] = useState(MouseAction.Off);

  const parentRef = useRef<HTMLDivElement>(null);
  const [parentDimensions, setParentDimensions] = useState<Dimensions | null>(
    null,
  );

  const groupRef = useRef<SVGGElement>(null);
  const baseRef = useRef<SVGGElement>(null);
  const overlayRef = useRef<SVGGElement>(null);
  const [position, setPosition] = useState<Position | null>(null);

  const overlayIsLoading = overlay !== undefined && overlayElement === null;
  const positionNotSet = position === null;
  const showLoading =
    baseElement === null || overlayIsLoading || positionNotSet;

  const replaceElement = useCallback(
    (ref: SVGGElement | null, element: HTMLElement | null) => {
      if (ref !== null) {
        if (element === null) {
          ref.replaceChildren();
        } else {
          ref.replaceChildren(element);
        }
      }
    },
    [],
  );

  useEffect(() => {
    replaceElement(baseRef.current, baseElement);
  }, [replaceElement, baseElement]);

  useEffect(() => {
    if (overlayElement !== null) {
      replaceElement(overlayRef.current, overlayElement);
    }
  }, [replaceElement, overlayElement]);

  const setDefaultPosition = useCallback(() => {
    if (overlay !== undefined) {
      if (
        baseDimensions !== null &&
        parentDimensions !== null &&
        overlayDimensions !== null
      ) {
        const overlayMinX = overlayDimensions.minX + overlay.placement.x;
        const overlayMinY = overlayDimensions.minY + overlay.placement.y;
        const minX = Math.min(overlayMinX, baseDimensions.minX);
        const maxX = Math.max(
          overlayMinX + overlayDimensions.width,
          baseDimensions.minX + baseDimensions.width,
        );
        const minY = Math.min(overlayMinY, baseDimensions.minY);
        const maxY = Math.max(
          overlayMinY + overlayDimensions.height,
          baseDimensions.minY + baseDimensions.height,
        );
        const dimensionsWithOverlay: Dimensions = {
          minX: minX,
          minY: minY,
          width: maxX - minX,
          height: maxY - minY,
        };
        setPosition(
          getDefaultPosition(dimensionsWithOverlay, parentDimensions, minScale),
        );
      }
    } else {
      if (baseDimensions !== null && parentDimensions !== null) {
        setPosition(
          getDefaultPosition(baseDimensions, parentDimensions, minScale),
        );
      }
    }
  }, [baseDimensions, minScale, overlay, overlayDimensions, parentDimensions]);

  useEffect(() => {
    if (positionNotSet || inert) {
      setDefaultPosition();
    }
  }, [inert, positionNotSet, setDefaultPosition]);

  useEffect(() => {
    if (
      focus !== null &&
      parentDimensions !== null &&
      baseDimensions !== null
    ) {
      const defaultPosition = getDefaultPosition(
        focus.dimensions,
        parentDimensions,
        minScale,
      );

      const focusX =
        focus.mountType == MountType.TOP
          ? focus.dimensions.minX - baseDimensions.minX
          : baseDimensions.minX +
            baseDimensions.width -
            focus.dimensions.minX -
            focus.dimensions.width;
      const focusY =
        baseDimensions.minY +
        baseDimensions.height -
        focus.dimensions.height -
        focus.dimensions.minY;
      setPosition({
        x: defaultPosition.x - focusX * defaultPosition.zoom,
        y: defaultPosition.y - focusY * defaultPosition.zoom,
        zoom: defaultPosition.zoom,
      });
    }
  }, [focus, minScale, parentDimensions, baseDimensions, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setFocus({ focus: null }));
    };
  }, [dispatch]);

  const zoom = useCallback(
    (scaleAmount: number, relativeX: number, relativeY: number) => {
      if (position !== null) {
        setPosition({
          x: (position.x - relativeX) * scaleAmount + relativeX,
          y: (position.y - relativeY) * scaleAmount + relativeY,
          zoom: position.zoom * scaleAmount,
        });
      }
    },
    [position],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      const scaleAmount = 1 - event.deltaY / 500;
      const relativeX = event.offsetX;
      const relativeY = event.offsetY;
      zoom(scaleAmount, relativeX, relativeY);
    },
    [zoom],
  );

  const move = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      if (mouseAction == MouseAction.MoveBase) {
        if (position !== null) {
          setPosition({
            ...position,
            x: position.x + event.movementX,
            y: position.y + event.movementY,
          });
        }
      } else if (mouseAction == MouseAction.MoveOverlay) {
        if (
          overlay !== undefined &&
          position !== null &&
          overlay.setPlacement !== undefined
        ) {
          const scalar = 1 / position.zoom;
          overlay.setPlacement({
            ...overlay.placement,
            x:
              overlay.placement.x +
              event.movementX *
                scalar *
                (overlay.placement.mount_type == MountType.TOP ? 1 : -1),
            y: overlay.placement.y - event.movementY * scalar,
          });
        }
      }
      setCursorLocation({ x: event.offsetX, y: event.offsetY });
    },
    [mouseAction, overlay, position],
  );

  const setMouseDown = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      if (
        overlayRef.current !== null &&
        event.target !== null &&
        overlayRef.current.contains(event.target as Node) &&
        overlay !== undefined &&
        overlay.setPlacement !== undefined
      ) {
        setMouseAction(MouseAction.MoveOverlay);
      } else {
        setMouseAction(MouseAction.MoveBase);
      }
    },
    [overlay],
  );

  const setMouseUp = useCallback(() => {
    setMouseAction(MouseAction.Off);
  }, []);

  useEffect(() => {
    const currentParentRef = parentRef.current;
    if (currentParentRef !== null) {
      currentParentRef.addEventListener("mousemove", move);
      if (!inert) {
        currentParentRef.addEventListener("wheel", handleWheel);
        currentParentRef.addEventListener("mousedown", setMouseDown);
        window.addEventListener("mouseup", setMouseUp);
      }
      return () => {
        currentParentRef.removeEventListener("mousemove", move);
        if (!inert) {
          currentParentRef.removeEventListener("wheel", handleWheel);
          currentParentRef.removeEventListener("mousedown", setMouseDown);
          window.removeEventListener("mouseup", setMouseUp);
        }
      };
    }
  }, [handleWheel, inert, move, setMouseDown, setMouseUp]);

  const handleResize = useCallback(() => {
    if (parentRef.current !== null) {
      const newDimensions = {
        // Subtract the border widths
        width: parentRef.current.offsetWidth - borderWidth,
        height: parentRef.current.offsetHeight - borderWidth,
        minX: borderWidth / 2,
        minY: borderWidth / 2,
      };
      setParentDimensions(newDimensions);
    }
  }, [borderWidth]);

  const handleZoomButtonClick = useCallback(
    (scaleAmount: number) => {
      if (parentDimensions !== null)
        zoom(
          1 + scaleAmount,
          parentDimensions.width / 2,
          parentDimensions.height / 2,
        );
    },
    [parentDimensions, zoom],
  );

  useEffect(() => {
    if (parentRef.current !== null) {
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
  }, [handleResize]);

  useEffect(() => {
    if (groupRef.current !== null && position !== null) {
      groupRef.current.setAttribute(
        "transform",
        `translate(${position.x} ${position.y}) scale(${position.zoom})`,
      );
    }
  }, [position]);

  useEffect(() => {
    if (
      overlayRef.current !== null &&
      overlay !== undefined &&
      baseDimensions !== null &&
      overlayDimensions !== null
    ) {
      const overlayX =
        (overlay.placement.mount_type == MountType.TOP
          ? overlay.placement.x - baseDimensions.minX
          : baseDimensions.minX + baseDimensions.width - overlay.placement.x) +
        overlayDimensions.minX;
      const overlayY =
        baseDimensions.minY +
        baseDimensions.height -
        overlay.placement.y -
        overlayDimensions.height -
        overlayDimensions.minY;
      const rotation =
        overlay.placement.rotation_degrees *
        (overlay.placement.mount_type == MountType.TOP ? 1 : -1);
      overlayRef.current.setAttribute(
        "transform",
        `translate(${overlayX} ${overlayY}) rotate(${-rotation} ${-overlayDimensions.minX} ${overlayDimensions.height + overlayDimensions.minY})`,
      );
    }
  }, [baseDimensions, overlay, overlayDimensions]);

  if (baseErrored || overlayErrored) {
    return <ErrorComponent />;
  }

  return (
    <SVGAppContainer>
      <SVGApp
        whiteBackground={whiteBackground}
        frameless={frameless}
        grabbable={inert}
      >
        {/* The svg is appended to this once it is loaded*/}
        <SVGContainer ref={parentRef} $hidden={showLoading}>
          <RootSVG>
            <g ref={groupRef}>
              <g ref={baseRef} />
              <g ref={overlayRef} fillOpacity={0.7} />
            </g>
          </RootSVG>
        </SVGContainer>
        {showLoading && (
          <LoadingIcon
            text={"Loading image..."}
            centerHorizontally
            centerVertically
            whiteBackground={whiteBackground}
            styleProps={{ zIndex: 2 }}
          />
        )}
        {title && <Title>{title}</Title>}
        {!showLoading && !frameless && (
          <Tools>
            <CursorLocation
              cursorLocation={cursorLocation}
              mountType={mountType || MountType.TOP}
              position={position}
              baseDimensions={baseDimensions}
            />
            {!inert && (
              <>
                <SVGViewerPill
                  onClick={() => handleZoomButtonClick(-DEFAULT_SCALE_AMOUNT)}
                >
                  -
                </SVGViewerPill>
                <SVGViewerPill
                  onClick={() => handleZoomButtonClick(DEFAULT_SCALE_AMOUNT)}
                >
                  +
                </SVGViewerPill>
                <SVGViewerPill onClick={setDefaultPosition}>
                  Reset position
                </SVGViewerPill>
              </>
            )}
          </Tools>
        )}
      </SVGApp>
    </SVGAppContainer>
  );
}
